import { useAtomValue } from "jotai/react";

import { useOnboarding } from "@/context/OnboardingContext";
import { MONTH_DAYS_DELTA, OnboardingStepEnum } from "@/domain/onboarding";
import { useOnboardingEntryPointData } from "@/hooks/useOnboardingEntryPointData";
import { onboardingShortCodeDataAtom } from "@/stores/atoms";
import { formatLongDate, getDaysUntilDate } from "@/utils/date";
import { OnboardingTextType } from "@/utils/onboarding";

// The intro step's clinic logo and copy for the current entry point and
// variant, independent of how a layout presents them.
export const useOnboardingIntroContent = () => {
  const onboardingShortCodeData = useAtomValue(onboardingShortCodeDataAtom);
  const { getI18nKey } = useOnboarding();

  const { i18nKeyContext, monthsToTurning65MinusDelta, payerName } =
    useOnboardingEntryPointData(OnboardingStepEnum.INTRO);

  const monthsToTurning65 =
    monthsToTurning65MinusDelta !== undefined
      ? Math.abs(monthsToTurning65MinusDelta)
      : undefined;

  const numberOfMonthsToRedetermination =
    onboardingShortCodeData?.entryPointType === "REDETERMINATION"
      ? Math.floor(
          getDaysUntilDate(onboardingShortCodeData.redeterminationDate) /
            MONTH_DAYS_DELTA,
        )
      : undefined;

  const numberOfMonthsToWorkRequirements =
    onboardingShortCodeData?.entryPointType === "RECURRING_WORK_REQUIREMENT"
      ? Math.floor(
          getDaysUntilDate(onboardingShortCodeData.workRequirementDate) /
            MONTH_DAYS_DELTA,
        )
      : undefined;
  const lapseDate =
    onboardingShortCodeData?.entryPointType === "LAPSE"
      ? onboardingShortCodeData.lapseDate
      : undefined;

  const title: OnboardingTextType = {
    i18nKey: getI18nKey("intro.title"),
    i18nOptions: {
      payerName,
      lapseDate: formatLongDate(lapseDate),
      monthsToTurning65,
      count:
        numberOfMonthsToRedetermination ??
        numberOfMonthsToWorkRequirements ??
        monthsToTurning65,
      context: i18nKeyContext,
    },
  };

  const description: OnboardingTextType = {
    i18nKey: getI18nKey("intro.description"),
    i18nOptions: { context: i18nKeyContext },
  };

  return {
    clinicLogoBase64: onboardingShortCodeData?.clinicLogoBase64,
    title,
    description,
  };
};
